// Catálogo de permisos del panel de administración: qué menús puede ver
// un rol y qué acciones puede ejecutar dentro de cada uno.
//
// Los códigos de acción (ACTIONS) están identificados como constantes en
// ./constants -- el catálogo completo de todos los que existen, con su
// significado -- para no repetir el string suelto acá ni en cada
// hasAction(menu, "....") de las páginas.
//
// El árbol de menús (PERMISSION_MENU_TREE) refleja exactamente la
// navegación de AdminLayout (incluyendo el grupo "Catálogo" con sus
// submenús) y vive fijo en código.
//
// Las acciones (Agregar/Modificar/Eliminar/Aprobar) NO están fijas aquí:
// vienen del esquema de catálogo "SACCIONES" (Catálogo > Esquemas /
// Clasificaciones), igual que cualquier otro esquema del sistema — no
// hay ninguna lista de respaldo en código. Quien arma el formulario de
// Roles las carga con
// `new Classification().getClassificationsByScheme(SACCIONES)` y se las
// pasa a `getAvailableActions`/`getAllActionKeys`. Si ese esquema
// todavía no existe (falta correr
// server/scripts/install-permission-scheme.js), la lista de acciones
// llega vacía y el formulario debe avisarlo, no inventar acciones.
//
// Esto vive en el cliente porque es lo que dibuja la pestaña
// "Permisos por Menú" en Roles y, después, lo que oculta/deshabilita
// menús y botones según el rol del usuario. El backend solo guarda las
// claves como texto libre (menuPermissions / actionPermissions).

import { ACTIONS } from "./constants";

// Árbol de menús, igual a la navegación de AdminLayout. Los nodos con
// `children` son agrupadores (no son una página en sí, como "Catálogo")
// y no tienen checkbox de menú ni acciones propias; sus hijos sí las
// tienen.
//
// `availableActions` limita qué acciones del esquema SACCIONES aplican a
// cada menú -- es importante que cada permiso sea específico y no se
// reutilice un mismo código genérico entre operaciones distintas. Por
// eso Inventario ya no ofrece ADD/MOD/DEL/APR (no tiene sentido "crear"
// o "eliminar" un movimiento de inventario como si fuera un registro
// cualquiera): ofrece ENTRY/EXIT/ADJUST, una acción específica por cada
// operación real (Entrada, Salida, Ajuste). Y Usuarios agrega
// ACTIVATE/ASSIGN_ROLES como permisos propios, separados de MOD (antes
// activar/desactivar una cuenta y asignarle roles compartían el mismo
// permiso que editar el nombre o el correo). Resumen (Dashboard), por la
// misma razón, no ofrece ADD/MOD/DEL -- ahí no se crea, edita ni elimina
// nada -- sino VIS y EXPORT (el botón "Exportar" del resumen anual).
// Un menú sin `availableActions` sigue mostrando el set genérico
// VIS/ADD/MOD/DEL (compatibilidad con lo que ya había).
export const PERMISSION_MENU_TREE = [
  {
    key: "dashboard",
    label: "Resumen",
    availableActions: [ACTIONS.VIS, ACTIONS.EXPORT],
  },
  {
    key: "products",
    label: "Productos",
    availableActions: [ACTIONS.VIS, ACTIONS.ADD, ACTIONS.MOD, ACTIONS.DEL],
  },
  {
    key: "categories",
    label: "Categorías",
    availableActions: [ACTIONS.VIS, ACTIONS.ADD, ACTIONS.MOD, ACTIONS.DEL],
  },
  {
    key: "inventory",
    label: "Inventario",
    availableActions: [
      ACTIONS.VIS,
      ACTIONS.ENTRY,
      ACTIONS.EXIT,
      ACTIONS.ADJUST,
    ],
  },
  {
    key: "catalog",
    label: "Catálogo",
    children: [
      {
        key: "catalog_schema",
        label: "Esquemas",
        availableActions: [ACTIONS.VIS, ACTIONS.ADD, ACTIONS.MOD, ACTIONS.DEL],
      },
      {
        key: "catalog_classification",
        label: "Clasificaciones",
        availableActions: [ACTIONS.VIS, ACTIONS.ADD, ACTIONS.MOD, ACTIONS.DEL],
      },
    ],
  },
  {
    key: "users",
    label: "Usuarios",
    availableActions: [
      ACTIONS.VIS,
      ACTIONS.ADD,
      ACTIONS.MOD,
      ACTIONS.DEL,
      ACTIONS.ACTIVATE,
      ACTIONS.ASSIGN_ROLES,
    ],
  },
  {
    key: "roles",
    label: "Roles",
    availableActions: [ACTIONS.VIS, ACTIONS.ADD, ACTIONS.MOD, ACTIONS.DEL],
  },
  {
    key: "settings",
    label: "Configuración",
    availableActions: [ACTIONS.VIS, ACTIONS.MOD],
  },
  {
    key: "audit",
    label: "Bitácora",
    availableActions: [ACTIONS.VIS],
  },
];

function flattenMenuLeaves(nodes) {
  return nodes.flatMap((node) =>
    node.children ? flattenMenuLeaves(node.children) : [node]
  );
}

const MENU_LEAVES = flattenMenuLeaves(PERMISSION_MENU_TREE);

export function getActionKey(menuKey, actionKey) {
  return `${menuKey}.${actionKey}`;
}

// `actions` es la lista {key, label} cargada desde el esquema SACCIONES
// (ver CreateRolePage.js). Si el esquema no está instalado todavía,
// llega vacía y no se puede marcar ninguna acción — no hay lista de
// respaldo en código.
export function getAvailableActions(node, actions = []) {
  if (!node.availableActions) {
    return actions;
  }

  return actions.filter((action) => node.availableActions.includes(action.key));
}

export function getAllMenuKeys() {
  return MENU_LEAVES.map((node) => node.key);
}

export function getMenuLeafByKey(menuKey) {
  return MENU_LEAVES.find((node) => node.key === menuKey) || null;
}

export function getAllActionKeys(actions = []) {
  return MENU_LEAVES.flatMap((node) =>
    getAvailableActions(node, actions).map((action) =>
      getActionKey(node.key, action.key)
    )
  );
}
